import { useEffect, useRef, useState } from "react";
import {
  type AutoscaleInfo,
  CandlestickSeries,
  ColorType,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
  type LogicalRange,
  type Time,
} from "lightweight-charts";
import type { Bar, IndicatorPayload, Pick, Timeframe } from "@alpha/shared";
import { toCandles, toLinePoints } from "../lib/chartData";

export interface Layers {
  sma50: boolean;
  sma200: boolean;
  ema21: boolean;
  bbands: boolean;
  rsi: boolean;
  macd: boolean;
  picks: boolean;
}

interface Props {
  rangeKey: string;
  bars: readonly Bar[];
  payload: IndicatorPayload | null;
  picks: readonly Pick[];
  tf: Timeframe;
  layers: Layers;
}

/** Brand colours resolved from the CSS variables, so the chart follows the dark and ivory themes. */
function palette() {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string) => cs.getPropertyValue(name).trim();
  const gain = v("--gain");
  const loss = v("--loss");
  const muted = v("--muted");
  return {
    bg: v("--bg"),
    text: muted,
    grid: v("--line"),
    font: v("--f-num"),
    up: gain,
    down: loss,
    volUp: withAlpha(gain, 0.35),
    volDown: withAlpha(loss, 0.35),
    volume: withAlpha(muted, 0.35),
    sma50: v("--champ"),
    sma200: v("--accent-hi"),
    ema21: gain,
    band: muted,
    rsi: v("--accent-hi"),
    macd: v("--accent-hi"),
    signal: v("--champ"),
    level: withAlpha(muted, 0.6),
    entry: v("--ink"),
    target: gain,
    stop: v("--signal"),
  };
}

/** "#rrggbb" or "#rgb" to rgba(); any other format is returned unchanged. */
export function withAlpha(color: string, alpha: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color);
  if (!m) return color;
  let h = m[1]!;
  if (h.length === 3) h = [...h].map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

interface Hover {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/** Candlesticks and volume, stored indicator overlays, RSI and MACD panes, and pick level lines. */
export function ChartView({ rangeKey, bars, payload, picks, tf, layers }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const ranges = useRef(new Map<string, LogicalRange>());
  const [hover, setHover] = useState<Hover | null>(null);
  const [themeTick, setThemeTick] = useState(0);

  useEffect(() => {
    const bump = () => setThemeTick((t) => t + 1);
    window.addEventListener("alpha-theme", bump);
    return () => window.removeEventListener("alpha-theme", bump);
  }, []);

  useEffect(() => {
    const el = host.current;
    if (!el || bars.length === 0) return;

    const C = palette();
    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: C.bg },
        textColor: C.text,
        fontFamily: C.font,
        fontSize: 11,
        attributionLogo: true, // TradingView attribution required by the Lightweight Charts licence
        panes: { separatorColor: C.grid, separatorHoverColor: C.level, enableResize: true },
      },
      grid: { vertLines: { color: C.grid }, horzLines: { color: C.grid } },
      rightPriceScale: { borderColor: C.grid },
      timeScale: { borderColor: C.grid, timeVisible: tf !== "1d", secondsVisible: false, rightOffset: 4 },
      crosshair: { vertLine: { color: C.level }, horzLine: { color: C.level } },
    });

    const candleData = toCandles(bars, tf);
    const candles = chart.addSeries(CandlestickSeries, {
      upColor: C.up, downColor: C.down, borderUpColor: C.up, borderDownColor: C.down, wickUpColor: C.up, wickDownColor: C.down,
    });
    candles.applyOptions({
      // Widen autoscale so the entry, target and stop lines of visible picks are always on screen.
      autoscaleInfoProvider: (original: () => AutoscaleInfo | null) => {
        const res = original();
        if (!res || !res.priceRange || !layers.picks || picks.length === 0) return res;
        const levels = picks.flatMap((p) => [Number(p.entry), Number(p.target), Number(p.stop)]).filter(Number.isFinite);
        return {
          ...res,
          priceRange: {
            minValue: Math.min(res.priceRange.minValue, ...levels),
            maxValue: Math.max(res.priceRange.maxValue, ...levels),
          },
        };
      },
    });
    candles.setData(candleData.map(({ time, open, high, low, close }) => ({ time: time as Time, open, high, low, close })));
    candles.priceScale().applyOptions({ scaleMargins: { top: 0.06, bottom: 0.22 } });

    const volume = chart.addSeries(HistogramSeries, { priceFormat: { type: "volume" }, priceScaleId: "vol", color: C.volume, lastValueVisible: false, priceLineVisible: false });
    volume.setData(candleData.map((c) => ({ time: c.time as Time, value: c.volume, color: c.close >= c.open ? C.volUp : C.volDown })));
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.84, bottom: 0 } });

    const firstUnix = Math.floor(Date.parse(bars[0]!.ts) / 1000);
    const line = (color: string, pane: number, key: string, values: readonly (number | null)[] | undefined, style: LineStyle = LineStyle.Solid) => {
      if (!payload || !values) return null;
      const s = chart.addSeries(LineSeries, { color, lineWidth: 1, lineStyle: style, lastValueVisible: false, priceLineVisible: false, crosshairMarkerVisible: false, title: key }, pane);
      s.setData(toLinePoints(payload.times, values, tf, firstUnix).map((p) => ({ time: p.time as Time, value: p.value })));
      return s;
    };

    if (payload) {
      if (layers.sma50) line(C.sma50, 0, "SMA 50", payload.sma50);
      if (layers.sma200) line(C.sma200, 0, "SMA 200", payload.sma200);
      if (layers.ema21) line(C.ema21, 0, "EMA 21", payload.ema21);
      if (layers.bbands && payload.bbands) {
        line(C.band, 0, "BB upper", payload.bbands.upper, LineStyle.Dashed);
        line(C.band, 0, "BB mid", payload.bbands.middle, LineStyle.Dotted);
        line(C.band, 0, "BB lower", payload.bbands.lower, LineStyle.Dashed);
      }
    }

    let pane = 1;
    if (payload && layers.rsi && payload.rsi) {
      const rsi = line(C.rsi, pane, "RSI 14", payload.rsi);
      for (const level of [70, 30]) {
        rsi?.createPriceLine({ price: level, color: C.level, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: false, title: "" });
      }
      pane++;
    }
    if (payload && layers.macd && payload.macd) {
      const hist = chart.addSeries(HistogramSeries, { color: C.volume, lastValueVisible: false, priceLineVisible: false, title: "MACD hist" }, pane);
      hist.setData(
        toLinePoints(payload.times, payload.macd.hist, tf, firstUnix).map((p) => ({
          time: p.time as Time, value: p.value, color: p.value >= 0 ? C.volUp : C.volDown,
        })),
      );
      line(C.macd, pane, "MACD", payload.macd.macd);
      line(C.signal, pane, "Signal", payload.macd.signal);
      pane++;
    }

    if (layers.picks) {
      for (const p of picks) {
        const tag = `${p.direction === "long" ? "L" : "S"} ${p.visibility}`;
        candles.createPriceLine({ price: Number(p.entry), color: C.entry, lineWidth: 1, lineStyle: LineStyle.Solid, axisLabelVisible: true, title: `${tag} entry` });
        candles.createPriceLine({ price: Number(p.target), color: C.target, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `${tag} target` });
        candles.createPriceLine({ price: Number(p.stop), color: C.stop, lineWidth: 1, lineStyle: LineStyle.Dashed, axisLabelVisible: true, title: `${tag} stop` });
      }
    }

    const panes = chart.panes();
    panes[0]?.setStretchFactor(4);
    for (let i = 1; i < panes.length; i++) panes[i]?.setStretchFactor(1.2);

    const saved = ranges.current.get(rangeKey);
    if (saved) chart.timeScale().setVisibleLogicalRange(saved);
    else chart.timeScale().setVisibleLogicalRange({ from: Math.max(0, candleData.length - 180), to: candleData.length + 4 });

    chart.subscribeCrosshairMove((param) => {
      const d = param.seriesData.get(candles) as { open: number; high: number; low: number; close: number } | undefined;
      const idx = param.time === undefined ? -1 : candleData.findIndex((c) => c.time === param.time);
      if (!d || idx < 0) {
        setHover(null);
        return;
      }
      setHover({ time: String(candleData[idx]!.time), open: d.open, high: d.high, low: d.low, close: d.close, volume: candleData[idx]!.volume });
    });

    return () => {
      const r = chart.timeScale().getVisibleLogicalRange();
      if (r) ranges.current.set(rangeKey, r);
      chart.remove();
    };
  }, [bars, payload, picks, tf, layers, rangeKey, themeTick]);

  return (
    <div className="chart-wrap">
      <div className="chart-hover" aria-live="off">
        {hover ? (
          <span className="num">
            {tf === "1d" ? hover.time : new Date(Number(hover.time) * 1000).toISOString().slice(0, 16).replace("T", " ")} O {hover.open} H {hover.high} L {hover.low} C{" "}
            {hover.close} V {new Intl.NumberFormat("en-US", { notation: "compact" }).format(hover.volume)}
          </span>
        ) : (
          <span className="muted">Hover the chart for bar values</span>
        )}
      </div>
      <div ref={host} className="chart-host" data-testid="chart-host" />
      <p className="attribution muted">
        Charting by{" "}
        <a href="https://www.tradingview.com/" target="_blank" rel="noopener noreferrer">
          TradingView Lightweight Charts
        </a>
        . Times shown in Africa/Johannesburg; stored data is UTC.
      </p>
    </div>
  );
}
